import React from "react";
import { useDraft } from "../../lib/draft-store";

const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+61", label: "🇦🇺 +61" },
];

const TIMES = [
  { key: "morning", label: "Morning" },
  { key: "afternoon", label: "Afternoon" },
  { key: "evening", label: "Evening" },
  { key: "anytime", label: "Anytime" },
  { key: "whatsapp", label: "WhatsApp only" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_NOTES = 1000;

export default function Step6Contact() {
  const { payload, setField } = useDraft();
  const c = payload?.contact || {};
  const setC = (k, v) => setField(["contact", k], v);

  const emailValid = !c.email || EMAIL_RE.test(c.email);
  const phoneValid = (() => {
    if (!c.phone) return false;
    const cc = c.country_code || "+91";
    if (cc === "+91") return /^\d{10}$/.test(String(c.phone).replace(/\s+/g, ""));
    return String(c.phone).replace(/\D/g, "").length >= 6;
  })();

  return (
    <div data-testid="plan-step-6">
      <h1 className="font-display text-3xl text-[#1A1A1A] sm:text-4xl">
        How can we reach you?
      </h1>
      <p className="mt-3 max-w-xl text-base text-[#1A1A1A]/65">
        Our concierge sends every quote within 24 hours.
      </p>

      <div className="mt-8 space-y-6">
        <div>
          <label htmlFor="full_name" className="block text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
            Full name *
          </label>
          <input
            id="full_name"
            type="text"
            data-testid="contact-name"
            value={c.full_name || ""}
            onChange={(e) => setC("full_name", e.target.value)}
            className="mt-2 w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
            placeholder="As you'd like to be addressed"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
            Email *
          </label>
          <input
            id="email"
            type="email"
            data-testid="contact-email"
            value={c.email || ""}
            onChange={(e) => setC("email", e.target.value)}
            className={[
              "mt-2 w-full rounded-lg border bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none",
              emailValid ? "border-[#EAE3D2] focus:border-[#C8A24B]" : "border-[#a33]",
            ].join(" ")}
            placeholder="you@example.com"
          />
          {!emailValid ? (
            <p data-testid="contact-email-error" className="mt-1 text-xs text-[#a33]">
              Please enter a valid email.
            </p>
          ) : null}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
            Phone *
          </label>
          <div className="mt-2 grid grid-cols-[110px,1fr] gap-2">
            <select
              data-testid="contact-country-code"
              value={c.country_code || "+91"}
              onChange={(e) => setC("country_code", e.target.value)}
              className="rounded-lg border border-[#EAE3D2] bg-white px-3 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
            >
              {COUNTRY_CODES.map((cc) => (
                <option key={cc.code} value={cc.code}>
                  {cc.label}
                </option>
              ))}
            </select>
            <input
              type="tel"
              inputMode="numeric"
              data-testid="contact-phone"
              value={c.phone || ""}
              onChange={(e) => setC("phone", e.target.value.replace(/[^\d]/g, ""))}
              className="rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
              placeholder={c.country_code === "+91" || !c.country_code ? "10-digit number" : "Phone"}
              maxLength={15}
            />
          </div>
        </div>

        <div>
          <p className="block text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
            Best time to reach
          </p>
          <div role="radiogroup" className="mt-3 flex flex-wrap gap-2">
            {TIMES.map((t) => {
              const active = c.best_time === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`contact-best-time-${t.key}`}
                  onClick={() => setC("best_time", t.key)}
                  className={[
                    "rounded-full border px-4 py-2 text-xs uppercase tracking-[0.16em] transition",
                    active
                      ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF7F2]"
                      : "border-[#1A1A1A]/15 text-[#1A1A1A]/80 hover:border-[#C8A24B] hover:text-[#C8A24B]",
                  ].join(" ")}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor="notes" className="flex items-center justify-between text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
            <span>Anything else?</span>
            <span data-testid="contact-notes-count">
              {(c.notes || "").length} / {MAX_NOTES}
            </span>
          </label>
          <textarea
            id="notes"
            data-testid="contact-notes"
            value={c.notes || ""}
            maxLength={MAX_NOTES}
            onChange={(e) => setC("notes", e.target.value)}
            rows={4}
            className="mt-2 w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
            placeholder="Mood, vibe, must-haves, family context — anything that helps."
          />
        </div>
      </div>
    </div>
  );
}

export function step6IsValid(payload) {
  const c = payload?.contact || {};
  if (!c.full_name) return false;
  if (!c.email || !EMAIL_RE.test(c.email)) return false;
  if (!c.phone) return false;
  const cc = c.country_code || "+91";
  if (cc === "+91" && !/^\d{10}$/.test(String(c.phone))) return false;
  return true;
}
